import { DateTime } from 'luxon';
import type {
  HierarchicalToolParent,
  HierarchicalToolsResponse,
  LicenseUsageBreakdownResponse,
  SoftwareCostsTotalsResponse,
  TimeSpentOnToolResponse
} from 'src/app/pages/software-cost-insights/software-cost-insights.service';
import type { NewStatsComcatResult } from 'src/app/services/stats/stats.service';
import { TimeuseScore } from 'src/models';
import type {
  LicenseUsageByUsersItem,
  LicenseUsageByUsersResponse,
  LicenseUsageQueryParams,
  ToolStatus
} from '../software-cost-insights/software-cost-insights.service';
import { DemoSeedService } from './demo-seed.service';
import { MockDataStore, User as MockUser } from './mock-data-store';

const ALL_USERS_FILTERS = new Set(['all-on-reports', 'all-self-on-reports']);

export interface DemoSciTool {
  id: string;
  name: string;
  entity: 'app' | 'domain';
  children?: { id: string, name: string, entity: 'app' | 'domain' }[];
}

/** Catalog of sample tools for Software Cost Insights (selector + default selection). */
export const DEMO_SCI_TOOLS: DemoSciTool[] = [
  {
    id: 'demo-sci-slack',
    name: 'Slack',
    entity: 'app',
    children: [
      { id: 'demo-sci-slack-desktop', name: 'Slack Desktop', entity: 'app' },
      { id: 'demo-sci-slack-web', name: 'slack.com', entity: 'domain' },
    ],
  },
  {
    id: 'demo-sci-zoom',
    name: 'Zoom',
    entity: 'app',
    children: [
      { id: 'demo-sci-zoom-desktop', name: 'Zoom', entity: 'app' },
      { id: 'demo-sci-zoom-web', name: 'zoom.us', entity: 'domain' },
    ],
  },
  {
    id: 'demo-sci-figma',
    name: 'Figma',
    entity: 'app',
    children: [
      { id: 'demo-sci-figma-app', name: 'Figma', entity: 'app' },
      { id: 'demo-sci-figma-web', name: 'figma.com', entity: 'domain' },
    ],
  },
  {
    id: 'demo-sci-github',
    name: 'GitHub',
    entity: 'domain',
    children: [
      { id: 'demo-sci-github-desktop', name: 'GitHub Desktop', entity: 'app' },
      { id: 'demo-sci-github-web', name: 'github.com', entity: 'domain' },
    ],
  },
  {
    id: 'demo-sci-notion',
    name: 'Notion',
    entity: 'app',
    children: [
      { id: 'demo-sci-notion-app', name: 'Notion', entity: 'app' },
      { id: 'demo-sci-notion-web', name: 'notion.so', entity: 'domain' },
    ],
  },
];

export function getDemoSciDefaultCategory(): NewStatsComcatResult {
  const tool = DEMO_SCI_TOOLS[0];
  return {
    comCat: tool.id,
    entity: tool.entity,
    name: tool.name,
    score: TimeuseScore.Productive,
    total: 72000,
  };
}

export function getDemoSciDefaultToolSelection() {
  const tool = DEMO_SCI_TOOLS[0];
  const selectedChildren = (tool.children || [{ id: tool.id, name: tool.name, entity: tool.entity }])
    .map(c => c.id);
  return {
    parentId: tool.id,
    parentName: tool.name,
    selectedChildren,
    allSelected: true,
  };
}

/** Sample-data comCat / hierarchy ids use this prefix so live mode can drop them after dismiss. */
export const DEMO_SCI_ID_PREFIX = 'demo-sci-';

export function isDemoSciComCatId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(DEMO_SCI_ID_PREFIX);
}

export function hasDemoSciToolSelection(selection: {
  parentId?: string;
  selectedChildren?: string[];
} | null | undefined): boolean {
  if (!selection) {
    return false;
  }
  if (isDemoSciComCatId(selection.parentId)) {
    return true;
  }
  return (selection.selectedChildren || []).some(isDemoSciComCatId);
}

function isAllUsersFilter(user: string | string[] | undefined): boolean {
  return typeof user === 'string' && ALL_USERS_FILTERS.has(user);
}

function filterUsers(
  options: { user?: string | string[] },
  users: MockUser[],
): MockUser[] {
  const filtered = users.filter(u => !u.archived);

  if (!options.user || isAllUsersFilter(options.user)) {
    return filtered;
  }

  const ids = Array.isArray(options.user)
    ? options.user.map(String)
    : String(options.user).split(',').filter(Boolean);

  if (ids.length === 0) {
    return [];
  }

  return filtered.filter(u => ids.includes(u.id));
}

function resolveDateRange(options: { from?: string, to?: string }) {
  const from = DateTime.fromISO(options.from || DateTime.now().minus({ days: 28 }).toISO()).startOf('day');
  const to = DateTime.fromISO(options.to || DateTime.now().toISO()).endOf('day');
  return { from, to };
}

function periodCount(from: DateTime, to: DateTime, period?: string): number {
  if (period === 'week') {
    return Math.max(1, Math.ceil(to.diff(from, 'weeks').weeks) || 1);
  }
  return Math.max(1, Math.floor(to.diff(from, 'days').days) + 1);
}

function resolveCategoryIds(params: Record<string, any>): string[] {
  if (typeof params?.category === 'string' && params.category.trim()) {
    return params.category.split(',').map((id: string) => id.trim()).filter(Boolean);
  }
  const prefilter = params?.['prefilter[comCat]'];
  if (Array.isArray(prefilter)) {
    return prefilter.filter((id: string) => id && id !== 'notFound');
  }
  if (typeof prefilter === 'string' && prefilter) {
    return [prefilter];
  }
  return [DEMO_SCI_TOOLS[0].id];
}

function toolStatusForUser(userId: string, seed: DemoSeedService, categoryId: string): ToolStatus {
  const roll = seed.int(0, 99, 'sci-status', userId, categoryId);
  const userNum = Number(/(\d+)$/.exec(userId)?.[1] || 1);
  if (userNum === 4 || roll < 15) {
    return 'Unused';
  }
  if (userNum === 3 || (roll >= 15 && roll < 40)) {
    return 'Rarely Used';
  }
  return 'Used';
}

function toolRatioForStatus(status: ToolStatus, seed: DemoSeedService, ...scope: (string | number)[]): number {
  if (status === 'Unused') {
    return 0;
  }
  if (status === 'Rarely Used') {
    return 0.005 + seed.float('sci-ratio-rare', ...scope) * 0.04;
  }
  return 0.08 + seed.float('sci-ratio-used', ...scope) * 0.35;
}

/** Flat category list for the non-hierarchical tool picker when sample data is active. */
export function buildMockSciComcatList(): NewStatsComcatResult[] {
  return DEMO_SCI_TOOLS.map((tool, index) => ({
    comCat: tool.id,
    entity: tool.entity,
    name: tool.name,
    score: TimeuseScore.Productive,
    total: 36000 + index * 7200,
  }));
}

export function buildMockHierarchicalTools(
  page = 0,
  search?: string,
  limit = 20,
): HierarchicalToolsResponse {
  const query = (search || '').trim().toLowerCase();
  const filtered = DEMO_SCI_TOOLS
    .filter(tool => !query
      || tool.name.toLowerCase().includes(query)
      || (tool.children || []).some(c => c.name.toLowerCase().includes(query)))
    .map((tool): HierarchicalToolParent => ({
      id: tool.id,
      name: tool.name,
      count: tool.children?.length || 1,
      children: (tool.children || [{ id: tool.id, name: tool.name, entity: tool.entity }]).map(child => ({
        id: child.id,
        entity: child.entity,
        name: child.name,
      })),
    }));

  const start = page * limit;
  const slice = filtered.slice(start, start + limit);
  const next = start + limit < filtered.length ? page + 1 : undefined;

  return {
    data: slice,
    paging: {
      totalCount: filtered.length,
      cur: page,
      limit,
      next,
      nItems: slice.length,
    },
  };
}

export function buildMockSciTotals(
  seed: DemoSeedService,
  params: Record<string, any> = {},
): SoftwareCostsTotalsResponse {
  const users = filterUsers(params, MockDataStore.getInstance().getAllUsers());
  const categories = resolveCategoryIds(params);
  const total = Math.max(users.length, 1);

  let used = 0;
  let unused = 0;
  let rarelyUsed = 0;

  users.forEach(user => {
    const status = toolStatusForUser(user.id, seed, categories[0]);
    if (status === 'Used') {
      used += 1;
    } else if (status === 'Unused') {
      unused += 1;
    } else {
      rarelyUsed += 1;
    }
  });

  // Keep totals consistent even when user filter is empty.
  if (users.length === 0) {
    return { total: 8, used: 5, unused: 2, rarelyUsed: 1 };
  }

  return { total, used, unused, rarelyUsed };
}

export function buildMockLicenseUsageBreakdown(
  seed: DemoSeedService,
  params: Record<string, any> = {},
): LicenseUsageBreakdownResponse {
  const users = filterUsers(params, MockDataStore.getInstance().getAllUsers());
  const { from, to } = resolveDateRange(params);
  const buckets = periodCount(from, to, params.period);
  const categories = resolveCategoryIds(params);
  const baseTotal = Math.max(users.length, 5);

  return Array.from({ length: buckets }, (_, index) => {
    const used = Math.max(1, Math.round(baseTotal * (0.45 + seed.float('sci-bd-used', categories[0], index) * 0.3)));
    const unused = Math.max(0, Math.round(baseTotal * (0.1 + seed.float('sci-bd-unused', categories[0], index) * 0.2)));
    const rarelyUsed = Math.max(0, baseTotal - used - unused);
    return {
      total: used + unused + rarelyUsed,
      used,
      unused,
      rarelyUsed,
    };
  });
}

export function buildMockTimeSpentOnTool(
  seed: DemoSeedService,
  params: Record<string, any> = {},
): TimeSpentOnToolResponse {
  const { from, to } = resolveDateRange(params);
  const buckets = periodCount(from, to, params.period);
  const categories = resolveCategoryIds(params);

  return Array.from({ length: buckets }, (_, index) => {
    const toolRatio = 0.05 + seed.float('sci-time-ratio', categories[0], index) * 0.25;
    const totalSec = 1800 + seed.int(0, 7200, 'sci-time-sec', categories[0], index);
    const totalTrackedSec = Math.round(totalSec / Math.max(toolRatio, 0.01));
    return [{
      total: totalSec,
      totalSec,
      toolRatio,
      totalTrackedSec,
    }];
  }) as unknown as TimeSpentOnToolResponse;
}

export function buildMockLicenseUsageByUsers(
  seed: DemoSeedService,
  params: LicenseUsageQueryParams,
): LicenseUsageByUsersResponse {
  const users = filterUsers(params, MockDataStore.getInstance().getAllUsers());
  const categories = resolveCategoryIds(params as Record<string, any>);
  const categoryId = categories[0] || DEMO_SCI_TOOLS[0].id;
  const tool = DEMO_SCI_TOOLS.find(t => t.id === categoryId || t.children?.some(c => c.id === categoryId))
    || DEMO_SCI_TOOLS[0];
  const child = tool.children?.find(c => c.id === categoryId) || tool.children?.[0];

  const items: LicenseUsageByUsersItem[] = users.map(user => {
    const status = toolStatusForUser(user.id, seed, categoryId);
    const toolRatio = toolRatioForStatus(status, seed, user.id, categoryId);
    const totalTrackedMins = 300 + seed.int(0, 600, 'sci-tracked', user.id, categoryId);
    const totalSec = Math.round(totalTrackedMins * 60 * toolRatio);
    return {
      size: 1,
      comCat: categoryId,
      userId: user.id,
      userName: user.name,
      email: user.email,
      totalSec,
      toolRatio,
      toolStatus: status,
      totalTrackedMins,
      totalTrackedSec: totalTrackedMins * 60,
      entity: child?.entity || tool.entity,
      name: child?.name || tool.name,
      categoryLastUsed: DateTime.now().minus({ days: seed.int(0, 10, 'sci-last', user.id) }).toISO(),
    };
  });

  const sort = params.sort || 'toolStatus';
  const sorted = [...items].sort((a, b) => {
    const desc = sort.startsWith('_');
    const key = desc ? sort.slice(1) : sort;
    const av = (a as any)[key];
    const bv = (b as any)[key];
    if (av === bv) {
      return 0;
    }
    const cmp = av > bv ? 1 : -1;
    return desc ? -cmp : cmp;
  });

  const page = Number(params.page) || 0;
  const limit = Number(params.limit) || 10;
  const slice = sorted.slice(page, page + limit);
  const next = page + limit < sorted.length ? page + limit : null;

  return {
    data: slice,
    paging: {
      totalCount: sorted.length,
      limit,
      page,
      next,
    },
  };
}
